import { useEffect, useState } from 'react';
import { cloudStorage } from '@telegram-apps/sdk-react';

import { PremiumCloudKeys } from '@/enums/storageEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import usePolicy from '@/hooks/usePolicy.ts';

interface UpsaleStorageData {
  firstShownAt: number;
  shownCount: number;
}

const MAX_SHOWN_COUNT = 4;

const SCHEDULE: Record<number, number> = {
  0: 2 * 60 * 60 * 1000, // 2h
  1: 24 * 60 * 60 * 1000, // 1 day
  2: 2 * 24 * 60 * 60 * 1000, // 2 days
  3: 4 * 24 * 60 * 60 * 1000 // 4 days
};

export const usePremiumUpsaleDiscoveryPopupControl = () => {
  const [premiumUpsaleOpen, setPremiumUpsaleOpen] = useState(false);

  const { premium } = useProfileStore();
  const { isActive } = usePolicy();

  const closePremiumUpsale = () => setPremiumUpsaleOpen(false);

  useEffect(() => {
    if (!premium.isExpired || !isActive) return;

    const checkAndOpen = async () => {
      let data: UpsaleStorageData | null = null;

      if (cloudStorage.getItem.isAvailable()) {
        try {
          const stored = await cloudStorage.getItem(
            PremiumCloudKeys.TELEGRAM_PREMIUM_UPSALE_MODAL_KEY
          );

          if (stored) {
            const parsed = JSON.parse(stored) as UpsaleStorageData;
            data = {
              firstShownAt: Number(parsed.firstShownAt),
              shownCount: Number(parsed.shownCount)
            };
          }
        } catch (_) {
          /* empty */
        }
      }

      const now = Date.now();

      if (!data) {
        const newData: UpsaleStorageData = {
          firstShownAt: now,
          shownCount: 0
        };

        if (cloudStorage.setItem.isAvailable()) {
          try {
            await cloudStorage.setItem(
              PremiumCloudKeys.TELEGRAM_PREMIUM_UPSALE_MODAL_KEY,
              JSON.stringify(newData)
            );
          } catch (_) {
            /* empty */
          }
        }

        return;
      }

      if (data.shownCount >= MAX_SHOWN_COUNT) return;

      const delay = SCHEDULE[data.shownCount] ?? 0;
      const nextShowTime = data.firstShownAt + delay;

      const shouldShow = now >= nextShowTime;

      if (!shouldShow) return;

      setPremiumUpsaleOpen(true);

      const newData: UpsaleStorageData = {
        firstShownAt: data.firstShownAt,
        shownCount: data.shownCount + 1
      };

      if (cloudStorage.setItem.isAvailable()) {
        try {
          await cloudStorage.setItem(
            PremiumCloudKeys.TELEGRAM_PREMIUM_UPSALE_MODAL_KEY,
            JSON.stringify(newData)
          );
        } catch (_) {
          /* empty */
        }
      }
    };

    void checkAndOpen();
  }, [premium.isExpired, isActive]);

  return {
    premiumUpsaleOpen,
    closePremiumUpsale
  };
};
